import { useState } from 'react'
import { supabase } from '../lib/supabase'

const styles = {
  container: {
    minHeight: '100vh',
    background: '#0a0a0f',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    fontFamily: "'DM Sans', sans-serif",
  },
  card: {
    background: '#13131a',
    border: '1px solid #1e1e2e',
    borderRadius: 16,
    padding: '48px 40px',
    width: '100%',
    maxWidth: 400,
  },
  logo: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 10,
    marginBottom: 8,
  },
  logoIcon: { fontSize: 28 },
  logoText: {
    fontSize: 28,
    fontWeight: 700,
    color: '#fff',
    letterSpacing: '-0.5px',
  },
  tagline: {
    color: '#6b6b80',
    fontSize: 14,
    marginBottom: 32,
    marginTop: 0,
    textAlign: 'center',
  },
  form: {
    display: 'flex',
    flexDirection: 'column',
    gap: 12,
  },
  input: {
    background: '#1a1a24',
    border: '1px solid #2a2a3a',
    borderRadius: 8,
    padding: '12px 16px',
    color: '#fff',
    fontSize: 14,
    outline: 'none',
  },
  error: {
    color: '#ff4d4d',
    fontSize: 13,
    margin: 0,
  },
  success: {
    color: '#22c55e',
    fontSize: 14,
    marginBottom: 24,
  },
  button: {
    background: '#4f46e5',
    color: '#fff',
    border: 'none',
    borderRadius: 8,
    padding: '13px 0',
    fontSize: 15,
    fontWeight: 600,
    cursor: 'pointer',
    marginTop: 4,
  },
  toggle: {
    background: 'none',
    border: 'none',
    color: '#6b6b80',
    fontSize: 13,
    cursor: 'pointer',
    marginTop: 20,
    width: '100%',
    textAlign: 'center',
  },
}

export default function Login() {
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [confirmPassword, setConfirmPassword] = useState('')
  const [isSignup, setIsSignup] = useState(false)
  const [forgotPassword, setForgotPassword] = useState(false)
  const [resetSent, setResetSent] = useState(false)
  const [loading, setLoading] = useState(false)
  const [error, setError] = useState('')

  async function handleSubmit(e) {
    e.preventDefault()
    setError('')

    if (isSignup && password !== confirmPassword) {
      setError('Passwords do not match')
      return
    }
    if (isSignup && password.length < 6) {
      setError('Password must be at least 6 characters')
      return
    }

    setLoading(true)
    try {
      if (isSignup) {
        const { error } = await supabase.auth.signUp({ email, password })
        if (error) throw error
      } else {
        const { error } = await supabase.auth.signInWithPassword({ email, password })
        if (error) throw error
      }
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  async function handleResetPassword(e) {
    e.preventDefault()
    setLoading(true)
    setError('')
    try {
      const { error } = await supabase.auth.resetPasswordForEmail(email, {
        redirectTo: 'https://app.jobconfirmed.com/reset-password'
      })
      if (error) throw error
      setResetSent(true)
    } catch (err) {
      setError(err.message)
    } finally {
      setLoading(false)
    }
  }

  function backToSignIn() {
    setForgotPassword(false)
    setResetSent(false)
    setError('')
  }

  function switchMode() {
    setIsSignup(!isSignup)
    setError('')
    setPassword('')
    setConfirmPassword('')
  }

  if (forgotPassword) {
    return (
      <div style={styles.container}>
        <div style={styles.card}>
          <div style={styles.logo}>
            <span style={styles.logoIcon}>JC</span>
            <span style={styles.logoText}>JobConfirmed</span>
          </div>

          {resetSent ? (
            <div>
              <p style={styles.success}>
                Check your email for a password reset link.
              </p>
              <button style={styles.button} onClick={backToSignIn}>
                Back to Sign In
              </button>
            </div>
          ) : (
            <div>
              <p style={styles.tagline}>Enter your email and we will send a reset link.</p>
              <form onSubmit={handleResetPassword} style={styles.form}>
                <input
                  style={styles.input}
                  type="email"
                  placeholder="Email address"
                  value={email}
                  onChange={e => setEmail(e.target.value)}
                  required
                />
                {error && <p style={styles.error}>{error}</p>}
                <button style={styles.button} type="submit" disabled={loading}>
                  {loading ? 'Sending...' : 'Send Reset Link'}
                </button>
              </form>
              <button style={styles.toggle} onClick={backToSignIn}>
                Back to Sign In
              </button>
            </div>
          )}
        </div>
      </div>
    )
  }

  return (
    <div style={styles.container}>
      <div style={styles.card}>
        <div style={styles.logo}>
          <span style={styles.logoIcon}>JC</span>
          <span style={styles.logoText}>JobConfirmed</span>
        </div>
        <p style={styles.tagline}>Stop losing jobs to no-shows.</p>

        <form onSubmit={handleSubmit} style={styles.form}>
          <input
            style={styles.input}
            type="email"
            placeholder="Email address"
            value={email}
            onChange={e => setEmail(e.target.value)}
            required
          />
          <input
            style={styles.input}
            type="password"
            placeholder="Password"
            value={password}
            onChange={e => setPassword(e.target.value)}
            required
          />
          {isSignup && (
            <input
              style={styles.input}
              type="password"
              placeholder="Confirm password"
              value={confirmPassword}
              onChange={e => setConfirmPassword(e.target.value)}
              required
            />
          )}
          {error && <p style={styles.error}>{error}</p>}
          <button style={styles.button} type="submit" disabled={loading}>
            {loading ? 'Loading...' : isSignup ? 'Create Account' : 'Sign In'}
          </button>
        </form>

        <button style={styles.toggle} onClick={switchMode}>
          {isSignup ? 'Already have an account? Sign in' : "Don't have an account? Sign up"}
        </button>

        {!isSignup && (
          <button style={{...styles.toggle, marginTop: 8}} onClick={() => setForgotPassword(true)}>
            Forgot password?
          </button>
        )}
      </div>
    </div>
  )
}
